import { useEffect, useState } from "react";
import { Plus, Flame, Building2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import api, { formatCLP } from "../lib/api";
import { toast } from "sonner";

let _branchesCache = null;
const branchesPromise = () => {
    if (_branchesCache) return Promise.resolve(_branchesCache);
    return api.get("/branches").then(({ data }) => {
        _branchesCache = data || [];
        return _branchesCache;
    }).catch(() => []);
};

export default function ProductCard({ product }) {
    const { add } = useCart();
    const [branches, setBranches] = useState([]);
    const [activeBranch, setActiveBranch] = useState(null);

    useEffect(() => {
        branchesPromise().then((bs) => {
            setBranches(bs);
        });
    }, []);

    const totalStock = product.stock || 0;
    const branchStock = product.branch_stock || {};
    const currentStock = activeBranch ? (branchStock[activeBranch.id] ?? 0) : totalStock;
    const lowStock = currentStock > 0 && currentStock <= 10;

    const onAdd = () => {
        if (currentStock <= 0) return;
        add(product, 1);
        toast.success(`¡${product.name} en tu carrito! 🌶️`, {
            description: activeBranch
                ? `Disponible en ${activeBranch.name}`
                : "Sigue antojándote, vamos por el regalo sorpresa",
        });
    };

    return (
        <div
            data-testid={`product-card-${product.id}`}
            className="bg-white rounded-3xl shadow-sm border border-border/40 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden group flex flex-col h-full"
        >
            <div className="relative aspect-square overflow-hidden bg-[#F8F5F0]">
                {product.image ? (
                    <>
                        <img
                            src={product.image}
                            alt={product.name}
                            className="object-cover w-full h-full transition-opacity duration-500 group-hover:opacity-0"
                            loading="lazy"
                        />
                        {product.image2 ? (
                            <img
                                src={product.image2}
                                alt={`${product.name} vista 2`}
                                className="absolute inset-0 object-cover w-full h-full opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                                loading="lazy"
                            />
                        ) : null}
                    </>
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-cocoa font-display text-4xl">🌶️</div>
                )}
                {product.featured && (
                    <span className="absolute top-2 left-2 bg-mexyellow text-cocoa text-[9px] sm:text-xs font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full shadow">
                        ★ <span className="hidden sm:inline">Destacado</span>
                    </span>
                )}
                {lowStock && (
                    <span className="absolute bottom-2 left-2 bg-mexred text-cocoa text-[9px] sm:text-xs font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full shadow flex items-center gap-1 animate-wobble">
                        <Flame className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {currentStock}
                    </span>
                )}
                {product.brand && (
                    <span className="absolute top-2 right-2 bg-white/90 backdrop-blur text-cocoa text-[9px] sm:text-xs font-medium px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-sm max-w-[60%] truncate">
                        {product.brand}
                    </span>
                )}
            </div>
            <div className="p-3 sm:p-4 flex flex-col flex-grow">
                <span className="text-[10px] sm:text-xs uppercase tracking-wider text-marigold font-semibold mb-1">
                    {product.category}
                </span>
                <h3 className="font-display font-medium text-cocoa text-sm sm:text-base md:text-lg mb-1 line-clamp-2">
                    {product.name}
                </h3>
                <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-2 mb-2">
                    {product.description}
                </p>

                {/* Branch selector */}
                {branches.length > 0 && (
                    <div className="mb-2">
                        <div className="flex flex-wrap gap-1">
                            <button
                                onClick={() => setActiveBranch(null)}
                                className={`text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full border transition-all ${
                                    !activeBranch
                                        ? "bg-cocoa text-white border-cocoa"
                                        : "bg-white text-cocoa border-border hover:border-cocoa"
                                }`}
                            >
                                Total ({totalStock})
                            </button>
                            {branches.filter((b) => b.active).map((b) => {
                                const bs = branchStock[b.id] ?? 0;
                                const isActive = activeBranch?.id === b.id;
                                return (
                                    <button
                                        key={b.id}
                                        onClick={() => setActiveBranch(b)}
                                        className={`text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full border transition-all flex items-center gap-1 ${
                                            isActive
                                                ? "bg-mexred text-cocoa border-mexred"
                                                : "bg-white text-cocoa border-border hover:border-mexred"
                                        }`}
                                        title={b.address}
                                    >
                                        <Building2 className="w-2.5 h-2.5" />
                                        {b.name} ({bs})
                                    </button>
                                );
                            })}
                        </div>
                        {activeBranch?.note && (
                            <p className="text-[9px] sm:text-[10px] text-muted-foreground italic mt-1 leading-tight">
                                ⚠️ {activeBranch.note}
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-auto flex items-center justify-between gap-2">
                    <span className="font-display font-bold text-cocoa text-base sm:text-xl">
                        {formatCLP(product.price)}
                    </span>
                    <button
                        data-testid={`add-to-cart-${product.id}`}
                        onClick={onAdd}
                        disabled={currentStock <= 0}
                        className="bg-mexred hover:bg-mexred_hover disabled:bg-muted disabled:text-muted-foreground text-cocoa rounded-full px-2.5 py-1 text-[11px] sm:px-4 sm:py-2 sm:text-sm font-semibold transition-all flex items-center gap-1 shadow-sm hover:shadow-md btn-bounce whitespace-nowrap shrink-0"
                    >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                        {currentStock <= 0 ? "Agotado" : "Lo quiero"}
                    </button>
                </div>
            </div>
        </div>
    );
}
